import { ChevronLeftIcon, EyeIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline'
import { GlobalConfigType } from 'actions'
import cloneDeep from 'clone-deep'
import { InputsTable } from 'components/InputsTable'
import { LayoutLoading } from 'components/LayoutLoading'
import { HeaderSelectionModal } from 'components/LoanPipelineHeaderConfigs/HeaderSelectionModal'
import { SaveChanges } from 'components/SaveChanges'
import { InputType } from 'config/input.type.constants'
import { useProductTypeOptions } from 'hooks/useProductTypeOptions'
import { IButtonType, taskInputs } from 'pages/Admin/ConditionsAndTemplates'
import { isPlaceholderSelected } from 'pages/Loan/Reports/types'
import { EmailAssigneeOptions, TaskAssigneeOptions } from 'pages/Tasks/constants'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { getLoanTypeMapForSelect } from 'reducers/loanDetail.reducer'
import { getAiPromptTemplates, getEmailTemplatesGroup, getOriginalTemplate, submitTask } from 'services'
import { getAiProjects } from 'services/apis/aiProjects'
import { Button, FormTable, Modal, Tooltip } from 'stories/components'
import { InputConvert, InputValidate, openConfigFieldAuditLog } from 'utils'
import { getEmailHtml } from 'utils/emailHtml'
import { RenderInput } from 'utils/RenderInput'

const STATUS_CHANGE_HISTORY_FIELDS = [
  'statusTo',
  'statusToRequireVerify',
  'statusListBeforeToUpdate',
  'productTypes',
  'visibleLogic',
] as const

import {
  aiPromptInputs,
  defaultInputs,
  emailInputs,
  includingInfoByAction,
  loanStatusRelationInputs,
  statusChangeRuleInputs,
} from './constants'
import {
  ActionType,
  ILoanActionStatusChangeRule,
  ILoanSubmissionActionConfig,
  isActionTypeForLoan,
  isActionTypeHasStatusTo,
} from './types'

const normalizeStatusChangeRules = (data: ILoanSubmissionActionConfig): ILoanActionStatusChangeRule[] => {
  if (data.statusChangeRules?.length) return cloneDeep(data.statusChangeRules)
  if (!data.statusTo) return []
  return [
    {
      id: 1,
      statusTo: data.statusTo,
      statusToRequireVerify: data.statusToRequireVerify,
      statusListBeforeToUpdate: data.statusListBeforeToUpdate,
    },
  ]
}

export const LoanSubmissionDetails = ({
  actionType,
  action,
  label,
  data,
  adminEmails,
  userGroups = [],
  onBack,
  onChanged,
  tempTasks,
}: {
  actionType: ActionType
  action: string
  label: string
  data: ILoanSubmissionActionConfig
  adminEmails: string[]
  userGroups?: { id: number; name: string }[]
  onBack: Function
  onChanged?: Function
  tempTasks: Array<any>
}) => {
  const { optionsMap: productTypeOptionsMap } = useProductTypeOptions()
  const [loanGlobalStatus, loanFieldProperties] = useSelector((state: any) => [
    state.loanGlobalStatus,
    state.globalConfigReducer[GlobalConfigType.LoanFields],
  ])
  const [tasks, setTasks] = useState(Array<any>)
  const [emails, setEmails] = useState<Array<any>>([])
  const [aiPrompts, setAiPrompts] = useState<Array<any>>([])
  const [statusChangeRules, setStatusChangeRules] = useState<ILoanActionStatusChangeRule[]>([])
  const [inputs, setInputs] = useState(defaultInputs())
  const [tInputs, setTInputs] = useState<Record<string, any>>({})
  const [eInputs, setEInputs] = useState<Record<string, any>>({})
  const [aInputs, setAInputs] = useState<Record<string, any>>({})
  const [isLoading, setIsLoading] = useState(false)
  const [emailTemplates, setEmailTemplates] = useState<any[]>([])
  const [aiPromptTemplates, setAiPromptTemplates] = useState<any[]>([])
  const [aiProjects, setAiProjects] = useState<any[]>([])
  const [showModal, setShowModal] = useState(false)
  const [previewData, setPreviewData] = useState<Record<string, any> | null>({})
  const [originalTemplate, setOriginalTemplate] = useState('')
  const [isShowDateSelectModal, setShowDateSelectModal] = useState<any>(null)
  const [hasUnsavedEmailChanges, setHasUnsavedEmailChanges] = useState(false)
  const [hasUnsavedTaskChanges, setHasUnsavedTaskChanges] = useState(false)
  const [hasUnsavedAiPromptChanges, setHasUnsavedAiPromptChanges] = useState(false)
  const [isChanged, setIsChanged] = useState(false)
  const isDirty = isChanged || hasUnsavedEmailChanges || hasUnsavedTaskChanges || hasUnsavedAiPromptChanges

  useEffect(() => {
    onChanged?.(isDirty)
  }, [isDirty])

  useEffect(() => {
    return () => onChanged?.(false)
  }, [])

  useEffect(() => {
    setIsLoading(true)
    getEmailTemplatesGroup('all').then((emails: any) => {
      setIsLoading(false)
      setEmailTemplates(emails)
      let temp: any = cloneDeep(inputs)
      Object.keys(temp).forEach((key) => {
        const value = (data as any)[key]
        temp[key].value = value
      })
      // Older actions stored one delay under Chain of Tasks and it also held the email batch.
      if (
        temp.emailsDelayMinutes &&
        (temp.emailsDelayMinutes.value === undefined ||
          temp.emailsDelayMinutes.value === null ||
          temp.emailsDelayMinutes.value === '')
      ) {
        const fallback = (data as any).emailDelayMinutes
        if (fallback) temp.emailsDelayMinutes.value = fallback
      }

      if (!isActionTypeHasStatusTo(actionType)) loanStatusRelationInputs.forEach((key) => (temp[key].visible = false))

      temp.conditions.visible = actionType == ActionType.LoanStatus

      setInputs(temp)
    })
    getOriginalTemplate().then((res) => {
      setOriginalTemplate(res)
    })
    getAiPromptTemplates().then((res) => setAiPromptTemplates(res?.data || []))
    getAiProjects().then((projects) => setAiProjects(projects || []))
  }, [action])

  useEffect(() => {
    const temp = cloneDeep(data.tasks)
    temp.map((item) => {
      if (item.needSendEmail == undefined) item.needSendEmail = true
    })
    setTasks(temp)
    if (data.emails)
      data.emails.forEach((email) => {
        if (Number(email.type) == email.type) return
        const emailId = emailTemplates.find((v) => v.subject == email.type)?.id
        if (emailId) email.type = `${emailId}`
      })
    setEmails(
      (data.emails || []).map((email: any) => ({
        ...email,
        emailCc: email.emailCc || [],
        emailBcc: email.emailBcc || [],
        emailExclude: email.emailExclude || [],
      })),
    )
    setAiPrompts(data.aiPrompts || [])
    setStatusChangeRules(normalizeStatusChangeRules(data))
  }, [data, emailTemplates])

  // `group:<id>` is what the backend expands into the group's member emails when the action fires,
  // so the routing follows whoever is in the group at that moment rather than a list frozen here.
  const groupOptions = useMemo(
    () =>
      (userGroups || []).reduce((acc: Record<string, string>, group) => {
        acc[`group:${group.id}`] = `[Group] ${group.name}`
        return acc
      }, {}),
    [userGroups],
  )

  const assigneeOptions = useMemo(() => {
    const options = { ...EmailAssigneeOptions(actionType), ...groupOptions }
    adminEmails.forEach((email) => (options[email] = email))
    return options
  }, [adminEmails, actionType, groupOptions])

  // Exclude is people only — roles stay on To/Cc/Bcc and expand at send time.
  const excludeOptions = useMemo(() => {
    const options: Record<string, string> = {}
    adminEmails.forEach((email) => (options[email] = email))
    return options
  }, [adminEmails])

  useEffect(() => {
    let temp: any = emailInputs()
    const options: Record<number, string> = {}
    emailTemplates.forEach((item: any) => (options[item.id] = item.subject))
    temp['type'].options = options
    temp['emailTo'].options = assigneeOptions
    temp['emailCc'].options = assigneeOptions
    temp['emailBcc'].options = assigneeOptions
    temp['emailExclude'].options = excludeOptions
    temp['includes'].options = includingInfoByAction(actionType)
    temp['channels'].visible = isActionTypeForLoan(actionType)
    temp['links'].render = renderECustomInput
    setEInputs(temp)
  }, [emailTemplates, assigneeOptions, excludeOptions, actionType])

  useEffect(() => {
    const templateOptions: Record<number, string> = {}
    aiPromptTemplates.forEach((item: any) => {
      templateOptions[item.id] = item.title
    })
    const projectOptions: Record<number, string> = {}
    aiProjects.forEach((project: any) => {
      projectOptions[project.id] = project.name
    })
    const temp: any = aiPromptInputs(loanFieldProperties)
    temp.templateId.options = templateOptions
    temp.projectId.options = projectOptions
    temp.channels.visible = isActionTypeForLoan(actionType)
    temp.productTypes.visible = isActionTypeForLoan(actionType)
    setAInputs(temp)
  }, [aiPromptTemplates, aiProjects, actionType, loanFieldProperties])

  const statusInputs = useMemo(
    () => statusChangeRuleInputs(loanGlobalStatus, loanFieldProperties),
    [loanGlobalStatus, loanFieldProperties],
  )

  const taskOptions = useMemo(() => {
    const options = { ...TaskAssigneeOptions(), ...groupOptions }
    adminEmails.forEach((email) => (options[email] = email))
    return options
  }, [adminEmails, groupOptions])

  useEffect(() => {
    let obj: any = {},
      IDObj: any = {
        0: 0,
      }
    tempTasks.map((task) => {
      obj[`${task.no}`] = `${task.description} - ${task.no}`
    })
    tasks.map((task) => {
      IDObj[task.id] = task.id
    })
    let temp: any = taskInputs()
    temp = {
      parentID: {
        title: 'Parent ID',
        inputType: 'select',
        hasDefaultOption: true,
        value: 0,
        options: IDObj,
        error: '',
        required: true,
        span: 2,
      },
      taskTemplateNo: {
        title: 'Choose Template',
        inputType: 'select',
        hasDefaultOption: true,
        options: obj,
        error: '',
        sort: true,
        sortKey: 'value',
      },
      ...temp,
      channels: {
        inputType: 'multiselect',
        title: 'Channel',
        options: getLoanTypeMapForSelect(),
        defaultSelected: true,
        visible: isActionTypeForLoan(actionType),
      },
      productTypes: {
        inputType: 'multiselect',
        title: 'Product Type',
        options: productTypeOptionsMap,
        defaultSelected: true,
        visible: isActionTypeForLoan(actionType),
      },
      needSendEmail: {
        inputType: 'checkbox',
        title: 'Send email for task',
        value: true,
      },
      noRepeat: {
        inputType: 'radio',
        title: 'Single or Always',
        options: {
          single: 'Single',
          always: 'Always',
        },
        value: 'always',
      },
      visibleLogic: {
        inputType: 'visibleLogic',
        title: 'Visible Logic',
        value: [],
        loanFieldProperties,
        options: {
          hasCustomOrigin: true,
          hasOtherOrigin: true,
          hasNotVisible: true,
        },
      },
    }
    temp['assignedTo'].options = taskOptions
    temp['assignedToSecondary'].options = taskOptions
    temp['assignedToManager'].options = taskOptions
    temp['replyCcUser'].options = taskOptions

    temp['dueDays'].additionalElements = ((onChange: Function) => {
      return (
        <div className="flex gap-2">
          <Tooltip message={`Select Date`}>
            <PencilSquareIcon
              className="w-4 h-4 cursor-pointer text-shade-blue"
              onClick={() => {
                setShowDateSelectModal(() => onChange)
              }}
            />
          </Tooltip>
        </div>
      )
    }) as any
    setTInputs(temp)
  }, [tempTasks, taskOptions, tasks, loanFieldProperties, productTypeOptionsMap])

  const onChange = async (key: string, value: any) => {
    const newInputs = cloneDeep(inputs)
    newInputs[key].value = InputConvert(newInputs[key], value)
    newInputs[key].error = ''
    setInputs(newInputs)
    setIsChanged(true)
  }

  const onSubmit = () => {
    if (hasUnsavedEmailChanges) {
      toast('Please submit or cancel the email form table changes first.', { type: 'warning' })
      return false
    }
    if (hasUnsavedTaskChanges) {
      toast('Please submit or cancel the task form table changes first.', { type: 'warning' })
      return false
    }
    if (hasUnsavedAiPromptChanges) {
      toast('Please submit or cancel the AI prompt form table changes first.', { type: 'warning' })
      return false
    }
    const newInputs = cloneDeep(inputs)
    let values: Record<string, any> = {}
    let hasError = false
    Object.keys(inputs).forEach((key) => {
      if (inputs[key].visible === false) return null
      newInputs[key].error = InputValidate(newInputs[key])
      let { value, error } = newInputs[key]
      if (error) hasError = true
      values[key] = value
    })
    setInputs(newInputs)
    if (hasError) return false
    values.tasks = tasks
    values.emails = emails
    values.aiPrompts = aiPrompts
    const normalizeDelay = (raw: any) => {
      const delayNum = raw === '' || raw === null || raw === undefined ? 0 : Number(raw)
      return Number.isFinite(delayNum) && delayNum > 0 ? delayNum : 0
    }
    values.emailsDelayMinutes = normalizeDelay(values.emailsDelayMinutes)
    values.emailDelayMinutes = normalizeDelay(values.emailDelayMinutes)
    if (isActionTypeHasStatusTo(actionType)) {
      values.statusChangeRules = statusChangeRules
      values.statusTo = null
      values.statusToRequireVerify = false
      values.statusListBeforeToUpdate = []
    }
    onBack(values)
    setIsChanged(false)
  }

  const handleBack = async () => {
    onBack(null)
  }

  const onUpdateStatusChangeRules = (rows: Record<string, any>[]) => {
    setStatusChangeRules(rows as ILoanActionStatusChangeRule[])
    setIsChanged(true)
  }

  const showStatusChangeFieldHistory = useCallback(
    (rowIndex: number, fieldKey: string, childLevel: number) => {
      const row = statusChangeRules[rowIndex]
      if (!row || row.id == null) return
      const fieldTitle = (statusInputs[fieldKey] as any)?.title || fieldKey
      openConfigFieldAuditLog(
        `loanAction|${actionType}|${action}|statusChange|${row.id}|${fieldKey}`,
        `${label} · Status Change #${row.id} · ${fieldTitle}`,
        childLevel,
      )
    },
    [action, actionType, label, statusChangeRules, statusInputs],
  )

  const onSubmitEmails = (currentId: any, values: Record<string, any>) => {
    return new Promise(async (resolve) => {
      let temp = cloneDeep(emails)
      values.emailCc = values.emailCc || []
      values.emailBcc = values.emailBcc || []
      values.emailExclude = values.emailExclude || []
      if (!currentId) {
        values = {
          id: Date.now(),
          ...values,
        }
        temp.push(values)
      } else {
        temp.map((item, index) => {
          if (item.id == currentId) {
            temp[index] = { ...temp[index], ...values }
          }
        })
      }
      setEmails(temp)
      setHasUnsavedEmailChanges(false)
      setIsChanged(true)
      resolve(values)
    })
  }

  const onRemoveEmails = async (id: any) => {
    return new Promise(async (resolve) => {
      let temp: any = []
      emails.map((item) => {
        if (item.id != id) temp.push(item)
      })
      setEmails(temp)
      setIsChanged(true)
      resolve(true)
    })
  }

  const onSubmitTasks = (currentId: any, values: Record<string, any>) => {
    values.noRepeat = values.noRepeat === 'single' ? true : false
    values.assignedToSecondary = values.assignedToSecondary || []
    return new Promise(async (resolve) => {
      let temp = cloneDeep(tasks)
      if (!currentId) {
        let ID = 1
        tasks.map((task) => {
          if (ID <= task.id) ID = task.id + 1
        })
        values = {
          id: ID,
          ...values,
        }
        if (values.taskTemplateNo) {
          temp.push(values)
        } else {
          setIsLoading(true)
          delete values.taskTemplateNo
          try {
            const res = await submitTask(0, values)
            setIsLoading(false)
            if (res.taskNo) {
              values.taskTemplateNo = res.taskNo
              temp.push(values)
            }
          } catch {
            setIsLoading(false)
          }
        }
      } else {
        temp.map((item, index) => {
          if (item.id == currentId) {
            if (item.id == values.parentID) toast(`Invalid Parent ID`, { type: 'error' })
            else temp[index] = { ...temp[index], ...values }
          }
        })
      }
      setTasks(temp)
      setHasUnsavedTaskChanges(false)
      setIsChanged(true)
      resolve(values)
    })
  }

  const onRemoveTasks = async (id: any) => {
    return new Promise(async (resolve) => {
      let temp: any = []
      let hasChild = false
      tasks.map((item) => {
        if (item.parentID == id) hasChild = true
        if (item.id != id) temp.push(item)
      })
      if (hasChild) {
        toast('Child Tasks Exist!', { type: 'error' })
        resolve(true)
      } else {
        setTasks(temp)
        setIsChanged(true)
        resolve(true)
      }
    })
  }

  const inputLogic = (inputs: Record<string, InputType>, ID?: number, key?: string) => {
    const templateNo = inputs['taskTemplateNo'].value
    inputs['description'].disabled = templateNo ? true : false
    if (key === 'taskTemplateNo') {
      let temp: any = taskInputs()
      Object.keys(temp).map((key) => {
        inputs[key].value = temp[key].value
      })

      tempTasks.map((item) => {
        if (item.no == templateNo) {
          Object.keys(inputs).map((key) => {
            if (
              !['taskTemplateNo', 'parentID', 'channels', 'productTypes', 'noRepeat', 'needSendEmail'].includes(key)
            ) {
              inputs[key].value = item[key] || temp[key]?.value
              inputs[key].error = ''
            }
          })
        }
      })
    }
    if (inputs.noRepeat.value === true) inputs.noRepeat.value = 'single'
    if ([undefined, false].includes(inputs.noRepeat.value)) inputs.noRepeat.value = 'always'
    return inputs
  }

  const inputELogic = (inputs: Record<string, InputType>, ID?: number, key?: string) => {
    if (key === 'type') {
      emailTemplates.map((email) => {
        if (email.id == inputs.type.value) {
          inputs.content.value = email.content
          inputs.links.value = email.links
        }
      })
    }
    return inputs
  }

  const onSubmitAiPrompts = (currentId: any, values: Record<string, any>) => {
    values.noRepeat = values.noRepeat === 'single'
    return new Promise(async (resolve) => {
      const temp = cloneDeep(aiPrompts)
      if (!currentId) {
        let ID = 1
        aiPrompts.forEach((row) => {
          if (ID <= row.id) ID = row.id + 1
        })
        temp.push({ id: ID, ...values })
      } else {
        temp.forEach((item, index) => {
          if (item.id == currentId) temp[index] = { ...temp[index], ...values }
        })
      }
      setAiPrompts(temp)
      setHasUnsavedAiPromptChanges(false)
      setIsChanged(true)
      resolve(values)
    })
  }

  const onRemoveAiPrompts = async (id: any) => {
    return new Promise(async (resolve) => {
      setAiPrompts(aiPrompts.filter((item) => item.id != id))
      setIsChanged(true)
      resolve(true)
    })
  }

  const inputAiLogic = (inputs: Record<string, InputType>, ID?: number, key?: string) => {
    if (key === 'templateId') {
      aiPromptTemplates.forEach((template) => {
        if (template.id == inputs.templateId.value) {
          inputs.prompt.value = template.prompt || ''
          if (template.projectId) inputs.projectId.value = template.projectId
        }
      })
    }
    if (inputs.noRepeat.value === true) inputs.noRepeat.value = 'single'
    if ([undefined, false].includes(inputs.noRepeat.value)) inputs.noRepeat.value = 'always'
    return inputs
  }

  const getAiPromptTemplateTitle = (templateId: number) =>
    aiPromptTemplates.find((template) => template.id == templateId)?.title || templateId

  const getAiProjectName = (projectId?: number) =>
    projectId ? aiProjects.find((project) => project.id == projectId)?.name || projectId : '- From template -'

  const getEmailType = (type: string) => {
    let rlt = ''
    emailTemplates.map((email) => {
      if (email.id == type) rlt = email.subject
    })
    return rlt
  }

  const renderECustomInput = (value: Record<string, any>[], onChange: Function) => {
    const onChangeLinkProps = (key: string, newValue: any, index: number) => {
      value[index][key] = newValue
      onChange(value)
    }

    const removeLink = (index: number) => {
      value.splice(index, 1)
      onChange(value)
    }

    const addLink = () => {
      if (!value) value = []
      value.push({ url: '', label: '', type: 'button' })
      onChange(value)
    }

    return (
      <div className="grid grid-cols-2 gap-4">
        {value?.map((item, index) => {
          const input: InputType = {
            inputType: 'text',
            title: '',
            value: item.url,
            prefix: 'Link:',
            indent: 12,
            showLinkDir: true,
          }
          input.additionalElements = (
            <>
              <input
                className="border-gray-300 border-[1px] px-1"
                value={item.label}
                onChange={(e: any) => onChangeLinkProps('label', e.target.value, index)}
                placeholder={`${IButtonType[item.type]} Label`}
              />
              <select
                name={`button-type-${index}`}
                id={`button-type-${index}`}
                className="py-0 pl-1 pr-6 text-[12.5px] leading-[16px] border-gray-300"
                value={item.type}
                onChange={(e) => onChangeLinkProps('type', e.target.value, index)}
              >
                {Object.keys(IButtonType).map((key) => {
                  return (
                    <option value={key} key={key}>
                      {IButtonType[key]}
                    </option>
                  )
                })}
              </select>
              <span className="cursor-pointer text-red-800" onClick={() => removeLink(index)}>
                <TrashIcon className="w-4 h-4" />
              </span>
            </>
          )
          return (
            <RenderInput
              input={input}
              Key={`url-${index}`}
              onChange={(key: string, value: any) => onChangeLinkProps('url', value, index)}
            />
          )
        })}
        <div className="col-span-full">
          <Button link onClick={addLink}>
            + Add Button & Link
          </Button>
        </div>
      </div>
    )
  }

  const renderAdditionalActions = (row: Record<string, any>) => {
    return (
      <div className="flex gap-1">
        <div
          className="text-shade-blue cursor-pointer hover-shadow1 p-1 rounded w-fit"
          onClick={() => {
            setShowModal(true)
            setPreviewData(row)
          }}
        >
          <EyeIcon className="w-4 h-4" />
        </div>
      </div>
    )
  }

  const previewHtml = useMemo(() => {
    if (!previewData) return ''
    const elementList: any[] = []
    previewData.includes?.map((elementKey: string) => {
      elementList.push({
        type: 'element3',
        data: [elementKey, `{{${elementKey}}}`],
      })
    })

    const message = getEmailHtml(
      [
        ...elementList,
        {
          type: 'element2',
          data: [
            `<div style='font-family:sans-serif; text-align: left'>${(previewData.content || '')
              .split('\n')
              .join('<br/>')}</div>`,
          ],
        },
        ...(previewData.links || []).map((link: any) => {
          return {
            type: link.type === 'button' ? 'element6' : 'link',
            data: [link.url, link.label],
          }
        }),
      ],
      originalTemplate,
    )
    return message
  }, [previewData])

  const getTaskAssigneeOption = (to: string) => {
    if (TaskAssigneeOptions()[to]) return TaskAssigneeOptions()[to]
    if (groupOptions[to]) return groupOptions[to]
    if (adminEmails.includes(to)) return to
    return ''
  }

  const getSelectedOptionText = (value: any, input: InputType) => {
    const options = (input.options || {}) as Record<string, string>
    const keys = Object.keys(options)
    const selectedKeys = Array.isArray(value)
      ? value
      : keys.filter((key) => value?.[key] === true || (input.defaultSelected && value?.[key] !== false))

    return selectedKeys.map((key: string) => options[key] || key).join(', ')
  }

  const getStatusRuleCellValue = (columnKey: string, row: Record<string, any>) => {
    if (['statusListBeforeToUpdate', 'productTypes'].includes(columnKey)) {
      return getSelectedOptionText(row[columnKey], statusInputs[columnKey])
    }
    return undefined
  }

  return (
    <div className="w-full relative">
      <LayoutLoading show={isLoading} />
      <div className="text-shade-blue flex items-center mb-2 font-bold">
        <ChevronLeftIcon className="w-4 h-4" />
        <span className="hover:underline cursor-pointer" onClick={handleBack}>
          Back
        </span>
      </div>
      <SaveChanges show={isDirty} onSave={onSubmit} />

      <div className="flex flex-col gap-4 mb-4">
        <p className="text-[18px] font-semibold">Action: {label}</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Object.keys(inputs).map((key) => {
            const input = inputs[key]
            if (input.visible === false) return null
            // Each delay is rendered under its section, ahead of that section's table.
            if (key === 'emailDelayMinutes' || key === 'emailsDelayMinutes') return null

            let rlt = []
            rlt.push(
              <div className={input.span ? `col-span-${input.span}` : ''} key={key}>
                <RenderInput input={input} Key={key} onChange={onChange} />
              </div>,
            )
            if (key === 'section2') {
              rlt.push(
                <div key="emailsDelayMinutes" className="col-span-1 md:col-span-2">
                  {inputs.emailsDelayMinutes && (
                    <RenderInput input={inputs.emailsDelayMinutes} Key="emailsDelayMinutes" onChange={onChange} />
                  )}
                </div>,
              )
              rlt.push(
                <div key={`email-table`} className={`col-span-2`}>
                  <FormTable
                    key="emails"
                    header={[
                      {
                        key: 'emailTo',
                        title: (
                          <div>
                            <div className="mb-1 border-b">To</div>
                            <div className="mb-1 border-b">Cc</div>
                            <div className="mb-1 border-b">Bcc</div>
                            <div>Exclude</div>
                          </div>
                        ),
                        value: (row: any) => {
                          const renderAssignees = (keys: string[] = []) =>
                            keys.map((email: string) => <div key={email}>{assigneeOptions[email] || email}</div>)
                          return (
                            <div>
                              <div className="mb-1 border-b">{renderAssignees(row.emailTo)}</div>
                              <div className="mb-1 border-b">{renderAssignees(row.emailCc)}</div>
                              <div className="mb-1 border-b">{renderAssignees(row.emailBcc)}</div>
                              <div>{renderAssignees(row.emailExclude)}</div>
                            </div>
                          )
                        },
                      },
                      {
                        key: 'content',
                        title: (
                          <div>
                            <div className="mb-1 border-b">Subject</div>
                            <div className="mb-1 border-b">Content</div>
                            <div>Loan Info</div>
                          </div>
                        ),
                        value: (row: any) => {
                          return (
                            <div>
                              <div className="mb-1 border-b">{getEmailType(row.type) || row.type}</div>
                              <div className="mb-1 border-b italic">
                                <div
                                  dangerouslySetInnerHTML={{
                                    __html: row.content?.replace(/\n/g, '<br />'),
                                  }}
                                />
                              </div>
                              <div>{row.includes?.map((key: string) => `${eInputs['includes'].options[key]}, `)}</div>
                            </div>
                          )
                        },
                      },
                    ]}
                    inputs={eInputs}
                    data={emails}
                    inputLogic={inputELogic}
                    onSubmit={(id, values) => onSubmitEmails(id, values)}
                    onRemove={(id) => onRemoveEmails(id)}
                    renderAdditionalActions={renderAdditionalActions}
                    onChangeInputs={() => setHasUnsavedEmailChanges(true)}
                    onCancel={() => setHasUnsavedEmailChanges(false)}
                  />
                </div>,
              )
            }
            if (key === 'section1') {
              rlt.push(
                <div key={`status-change-rules-table`} className={`col-span-2`}>
                  <InputsTable
                    title=""
                    inputs={statusInputs}
                    rows={statusChangeRules}
                    onUpdate={onUpdateStatusChangeRules}
                    columnKeys={['statusTo', 'statusToRequireVerify', 'statusListBeforeToUpdate', 'productTypes']}
                    addRowTitle="Add Status Change"
                    editRowTitle="Edit Status Change"
                    confirmRemoveMessage="Are you sure you want to remove this status change?"
                    formClassName="w-196 grid gap-4 grid-cols-2 text-sm mb-60"
                    getCellValue={getStatusRuleCellValue}
                    historyFieldKeys={[...STATUS_CHANGE_HISTORY_FIELDS]}
                    onShowRowFieldHistory={showStatusChangeFieldHistory}
                  />
                </div>,
              )
            }
            if (key === 'section3') {
              rlt.push(
                <div key="emailDelayMinutes" className="col-span-1 md:col-span-2">
                  {inputs.emailDelayMinutes && (
                    <RenderInput input={inputs.emailDelayMinutes} Key="emailDelayMinutes" onChange={onChange} />
                  )}
                </div>,
              )
              rlt.push(
                <div key={`section3-table`} className={`col-span-2`}>
                  <FormTable
                    key="tasks"
                    header={[
                      {
                        key: 'Id-ParentID',
                        title: (
                          <div>
                            <div className="border-b mb-1">Task ID</div>Parent ID
                          </div>
                        ),
                        value: (row: any) => {
                          return (
                            <div>
                              <div className="border-b mb-1">{row.id}</div>
                              <div>{row.parentID}</div>
                            </div>
                          )
                        },
                      },
                      { key: 'taskTemplateNo', title: 'Template No' },
                      {
                        key: 'description',
                        title: (
                          <div>
                            <div className="border-b mb-1">Description</div>Notes
                          </div>
                        ),
                        value: (row: any) => {
                          return (
                            <div>
                              <div className="border-b mb-1">{row.description}</div>
                              <div>{row.notes}</div>
                            </div>
                          )
                        },
                      },
                      {
                        key: 'assignedTo',
                        title: (
                          <div>
                            <div className="border-b mb-1">Assigned to</div>
                            <div className="border-b mb-1">Secondary</div>Manager
                          </div>
                        ),
                        value: (row: any) => {
                          return (
                            <div>
                              <div className="border-b mb-1">
                                {(row.assignedTo || [])
                                  .map((to: string) => getTaskAssigneeOption(to))
                                  .filter((v: string) => !!v)
                                  .join(', ')}
                              </div>
                              <div className="border-b mb-1">
                                {(row.assignedToSecondary || [])
                                  .map((to: string) => getTaskAssigneeOption(to))
                                  .filter((v: string) => !!v)
                                  .join(', ') || '—'}
                              </div>
                              <div>
                                {(row.assignedToManager || [])
                                  .map((to: string) => getTaskAssigneeOption(to))
                                  .filter((v: string) => !!v)
                                  .join(', ')}
                              </div>
                            </div>
                          )
                        },
                      },
                      {
                        key: 'dueDays',
                        title: (
                          <div>
                            <div className="border-b mb-1">Reply CC User</div>Due Days
                          </div>
                        ),
                        value: (row: any) => (
                          <div>
                            <div className="border-b mb-1">
                              {row.replyCcUser
                                ?.map((to: string) => getTaskAssigneeOption(to))
                                .filter((v: string) => !!v)
                                .join(', ')}
                            </div>
                            <div>{row.dueDays}</div>
                          </div>
                        ),
                      },
                    ]}
                    inputs={tInputs}
                    inputLogic={inputLogic}
                    data={tasks}
                    onSubmit={(id, values) => onSubmitTasks(id, values)}
                    onRemove={(id) => onRemoveTasks(id)}
                    noText="No"
                    onChangeInputs={() => setHasUnsavedTaskChanges(true)}
                    onCancel={() => setHasUnsavedTaskChanges(false)}
                  />
                </div>,
              )
            }
            if (key === 'section4') {
              rlt.push(
                <div key={`section4-table`} className={`col-span-2`}>
                  <FormTable
                    key="aiPrompts"
                    header={[
                      { key: 'id', title: 'No', value: (row: any) => row.id },
                      {
                        key: 'templateId',
                        title: (
                          <div>
                            <div className="border-b mb-1">Template</div>
                            <div>AI Project</div>
                          </div>
                        ),
                        value: (row: any) => (
                          <div>
                            <div className="border-b mb-1">{getAiPromptTemplateTitle(row.templateId)}</div>
                            <div>{getAiProjectName(row.projectId)}</div>
                          </div>
                        ),
                      },
                      {
                        key: 'prompt',
                        title: 'Prompt',
                        value: (row: any) => (
                          <div className="max-w-md truncate whitespace-pre-wrap">{row.prompt || '-'}</div>
                        ),
                      },
                      {
                        key: 'filters',
                        title: (
                          <div>
                            <div className="border-b mb-1">Channel</div>
                            <div>Product Type</div>
                          </div>
                        ),
                        value: (row: any) => (
                          <div>
                            <div className="border-b mb-1">{getSelectedOptionText(row.channels, aInputs.channels)}</div>
                            <div>{getSelectedOptionText(row.productTypes, aInputs.productTypes)}</div>
                          </div>
                        ),
                      },
                    ]}
                    inputs={aInputs}
                    inputLogic={inputAiLogic}
                    data={aiPrompts}
                    onSubmit={(id, values) => onSubmitAiPrompts(id, values)}
                    onRemove={(id) => onRemoveAiPrompts(id)}
                    noText="No"
                    onChangeInputs={() => setHasUnsavedAiPromptChanges(true)}
                    onCancel={() => setHasUnsavedAiPromptChanges(false)}
                  />
                </div>,
              )
            }
            return rlt
          })}
        </div>
      </div>

      <Button onClick={onSubmit}>Save</Button>
      {showModal && previewData && (
        <Modal
          title={'Email Preview'}
          isOpen
          onClose={() => {
            setShowModal(false)
            setPreviewData(null)
          }}
          titleOkay=""
          titleCancel=""
        >
          <div dangerouslySetInnerHTML={{ __html: previewHtml || '' }}></div>
        </Modal>
      )}

      <HeaderSelectionModal
        isOpen={!!isShowDateSelectModal}
        multiselect={false}
        type="date"
        onClose={() => setShowDateSelectModal(null)}
        onSelectPlaceholder={(value) => {
          isShowDateSelectModal(value)
          setShowDateSelectModal(null)
        }}
        onItemsSubmit={() => {}}
        selectedCell={inputs.dueDays?.value}
        checkSelected={(v: string, title: string) => isPlaceholderSelected(v, title, inputs.dueDays?.value)}
      />
    </div>
  )
}
